// @flow

import type { AuthBloc } from "../../blocs/authBloc";
import type Vendor from "../../models/Vendor/Vendor";
import { apiAxios } from "../../config/AxiosConfig";
import {
  CreateVendorRequest,
  UpdateVendorRequest,
  VendorAttachment
} from "../../models/Vendor/Vendor";
import { AppToaster } from "../../components/Toaster/Toaster";
import { Intent } from "@blueprintjs/core";
import history from "../../history";

class VendorApi {
  authBloc: AuthBloc;

  constructor(authBloc: AuthBloc) {
    this.authBloc = authBloc;
  }

  async getAll(): Promise<Array<Vendor>> {
    const tenantId = this.authBloc.getCurrentTenant();

    const response = await apiAxios.get(
      `/tenants/${tenantId}/vendors`,
      this.buildHeaders()
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(response.status);
    }
  }

  async createVendor(
    createVendorRequest: CreateVendorRequest
  ): Promise<Vendor> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.post(
        `/tenants/${tenantId}/vendors`,
        createVendorRequest,
        {...this.buildHeaders(), skipErrorToast: true}
      );

      if (response.status === 201) {
        history.goBack();
        setTimeout(() => {
          AppToaster.show({
            message: "Contact Created",
            intent: Intent.PRIMARY
          });
        }, 300);

        return response.data;
      }
      else if (response.status === 409)
      {
        AppToaster.show({
          message: "Vendor Already Exist",
          intent: Intent.DANGER
        });
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to create contact. Please try again.',
        intent: Intent.DANGER
      });
    }
  }

  async updateVendor(
    updateVendorRequest: UpdateVendorRequest
  ): Promise<Vendor> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();
      const response = await apiAxios.patch(
        `/tenants/${tenantId}/vendors/${updateVendorRequest.id}`,
        updateVendorRequest,
        {...this.buildHeaders(), skipErrorToast: true}
      );

      if (response.status === 204) {
        history.goBack();
        setTimeout(() => {
          AppToaster.show({
            message: "Contact Updated",
            intent: Intent.PRIMARY
          });
        }, 300);

        return response.data;
      } else {
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to update contact. Please try again.',
        intent: Intent.DANGER
      });
    }
  }

  async removeVendorFromList(vendorId: string): Promise<Vendor> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.delete(
        `/tenants/${tenantId}/vendors/${vendorId}`,
        {...this.buildHeaders(), skipErrorToast: true}
      );

      if (response.status === 200) {
        AppToaster.show({
          message: "Contact Deleted",
          intent: Intent.PRIMARY
        });
        return response.data;
      } else {
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to delete contact. Please try again.',
        intent: Intent.DANGER
      });
    }
  }
  async vendorAttachments(attachments: VendorAttachment): Promise<Vendor> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.post(
        `/tenants/${tenantId}/vendors/attachments`,
        attachments,
        {...this.buildHeaders(), skipErrorToast: true}
      );
        return response.data;
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to upload attachment. Please try again.',
        intent: Intent.DANGER
      });
    }
  }
  buildHeaders = () => {
    const accessToken = this.authBloc.getCurrentToken();
    const auth = "Bearer " + accessToken;
    return { headers: { Authorization: auth } };
  };
}

export default VendorApi;
